/**
 * User-facing text for failed API calls. FastAPI returns { detail: string | { code, message } }.
 */
export const MEMBERSHIP_REVOKED_FALLBACK =
  'Your account access has been removed. Please contact your company administrator to restore the access you need.';

/**
 * FastAPI uses `detail`; some legacy/JSON routes use `error`.
 * @param {object} [data] - parsed JSON body
 * @returns {string|object|Array|null}
 */
function pickErrorPayload(data) {
  if (!data) return null;
  if (data.detail !== undefined) return data.detail;
  if (data.error !== undefined) return data.error;
  return null;
}

/**
 * @param {number} status - HTTP status
 * @param {object} [data] - parsed JSON body
 * @param {string} [fallback] - if nothing matches
 * @returns {string}
 */
export function getUserFacingErrorMessage(status, data, fallback = 'Something went wrong. Please try again.') {
  const raw = pickErrorPayload(data);
  if (raw && typeof raw === 'object' && raw !== null && typeof raw.message === 'string' && raw.message) {
    return raw.message;
  }
  if (typeof raw === 'string' && raw) {
    if (
      status === 403 &&
      (raw.includes('Company membership') ||
        raw.includes('No company membership') ||
        raw.includes('deactivated'))
    ) {
      if (raw.toLowerCase().includes('deactivated')) {
        return 'Your account has been deactivated.';
      }
      return MEMBERSHIP_REVOKED_FALLBACK;
    }
    return raw;
  }
  if (status === 401) {
    const p = pickErrorPayload(data);
    if (typeof p === 'string' && p) return p;
    if (p && typeof p === 'object' && p.message) return p.message;
    return 'Authentication failed. Please sign in again.';
  }
  if (status === 403) {
    return MEMBERSHIP_REVOKED_FALLBACK;
  }
  if (status === 404) {
    return 'The requested item was not found.';
  }
  return fallback;
}

/**
 * @param {Response} response
 * @returns {Promise<string>} User-facing error line
 */
export async function getUserFacingErrorMessageFromResponse(response) {
  let data = {};
  try {
    data = await response.json();
  } catch {
    // ignore
  }
  return getUserFacingErrorMessage(response.status, data, `Request failed (${response.status})`);
}

/**
 * If `response` is not OK, throws an Error with the same user-facing text as for suspended
 * membership and other server `detail` payloads. Does not read the body when ok.
 * @param {Response} response
 * @returns {Promise<Response>} The same response when ok
 */
export async function throwIfNotOk(response) {
  if (response.ok) return response;
  const message = await getUserFacingErrorMessageFromResponse(response.clone());
  const err = new Error(message);
  err.status = response.status;
  throw err;
}
